import '/src/scss/style.scss';

if (import.meta.hot) {
	import.meta.hot.accept();
}

import { loadApp, loadModule, unloadModule } from '@/core';
import { eventBus } from '@spon/plugins';
import 'lazysizes';
import 'lazysizes/plugins/respimg/ls.respimg';
import barba from '@barba/core';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

import Header from '@/globals/Header';
import Footer from '@/globals/Footer';
import SetVh from '@/globals/SetVh';
import Scroll from '@/globals/Scroll';
import ScrollText from '@/globals/ScrollText';
import ScrollColor from '@/globals/ScrollColorTrigger';
import CardHover from '@/globals/CardHover';
import MudCurveExperience from '@/globals/MudCurveExperience';

import webfontloader from '@/plugins/webfontloader';
import detectTab from '@/utils/detectTab';
import activeLinks from '@/utils/activeLinks';
import localLinks from '@/utils/localLinks';

import '@/plugins/logger';

// Patch requestAnimationFrame to use Tempus
// Tempus.patch();

window.deviceType = window.matchMedia('(pointer: coarse)').matches
	? 'mobile'
	: 'desktop';

// tell Barba to use the prefetch plugin

const scrollPositions = {};
const $rainbowContainer = document.querySelector('.bg-rainbow-container');
const $rainbow = document.querySelector('.bg-rainbow');

const ExperienceController =
	window.deviceType === 'mobile'
		? null
		: new MudCurveExperience(document.getElementById('canvas'), {
				autoStart: true,
				uniforms: {
					distance: 34,
					strength: 1,
				},
			});

// const moduleLoader = name => import(`./behaviours/${name}.js`);
// const app = loadApp(moduleLoader, document.body);

const app = loadApp(name => import(`./behaviours/${name}.js`), document.body); // eslint-disable-line

loadModule({
	module: Scroll,
	id: 'scroll',
	node: document.body,
	keepAlive: true,
});

loadModule({
	module: Header,
	id: 'header',
	node: document.getElementById('header'),
	keepAlive: true,
});

loadModule({
	module: SetVh,
	id: 'set-vh',
	node: document.body,
	keepAlive: true,
});

loadModule({
	module: Footer,
	id: 'footer',
	node: document.getElementById('footer'),
});

loadModule({
	module: ScrollText,
	id: 'scroll-text',
	node: document.body,
});

loadModule({
	module: ScrollColor,
	id: 'scroll-color',
	node: document.body,
});

loadModule({
	module: CardHover,
	id: 'card-hover',
	node: document.body,
});

app.hydrate();

webfontloader();
activeLinks(window.location.pathname);
localLinks(document.body);

barba.init({
	requestError: (trigger, action, url, response) => {
		console.log('barba requestError', trigger, action, url, response);
		// go to a custom 404 page if server respond with a 404 response status
		if (
			action === 'click' &&
			response.status &&
			(response.status === 404 || response.status === 500)
		) {
			return barba.force(`/${response.status}`);
			// barba.go('/404'); // not working...
		}
	},
	cacheFirstPage: true,
	cacheIgnore: false,
	debug: true,
	prefetchIgnore: false,
	prevent: ({ el }) => {
		if (!el) return false;
		if (el.closest('.yii-debug-toolbar')) return true;
		return el.classList && el.classList.contains('no-barba');
	},
	preventRunning: true,
	timeout: 5000,
	transitions: [
		{
			name: 'default-transition',
			sync: false,
			async leave(data) {
				const {
					trigger,
					next: {
						url: { path },
					},
				} = data;

				await new Promise(resolve => {
					gsap.to($rainbow, {
						duration: 0.5,
						y: '-25%',
						ease: 'power2.out',
						onComplete: () => {
							window.lenis.scrollTo('top', {
								duration: 0.1,
								force: true,
								lock: true,
								onComplete: () => {
									resolve();
								},
							});
						},
					});
				});
			},
			enter(data) {
				const {
					trigger,
					current: {
						url: { path: currentPath },
					},
					next: {
						url: { path },
					},
				} = data;

				const saved = scrollPositions[`${path}`] || 0;

				console.log('enter', path);

				const outAnimation = () => {
					return gsap
						.to($rainbow, {
							duration: 0.5,
							delay: 0.1,
							y: '-150%',
							ease: 'power2.in',
						})
						.then(() => {
							gsap.set($rainbow, { y: '50%' });
						});
				};
				if (
					saved !== 0 &&
					trigger === 'back' &&
					currentPath !== '/happenings'
				) {
					window.lenis.scrollTo(saved, {
						immediate: true,
						force: true,
						lock: true,
						onComplete: () => {
							outAnimation();
						},
					});
				} else {
					outAnimation();
				}
			},
		},
		// {
		// 	name: 'happenings-listing',
		// 	from: { namespace: 'happeningsListing' },
		// 	to: { namespace: 'happeningsListing' },
		// 	enter(data) {
		// 		return false;
		// 	},
		// 	leave(data) {
		// 		return false;
		// 	},
		// },
	],
});

barba.hooks.leave(data => {
	const {
		url: { path },
	} = data.current;

	scrollPositions[`${path}`] = window.lenis.scroll ?? 0;
	console.log('scrollPositions', scrollPositions);
});

barba.hooks.afterLeave(data => {
	window.lenis.scrollTo(0, {
		duration: 0.1,
		force: true,
		lock: true,
	});

	app.destroy();
	if (ExperienceController) ExperienceController.destroy();

	eventBus.emit('barba:afterLeave', data);
});

barba.hooks.beforeEnter(data => {
	const { container } = data.next;

	app.hydrate(container);
});

// unlock the scroll, in order to let the user be able to scroll again
barba.hooks.enter(data => {
	const {
		url: { path },
		container,
	} = data.next;

	const $header = document.getElementById('header');
	$header.classList.remove('not-at-top');

	const newPath = path || window.location.origin;

	activeLinks(newPath);
	localLinks(container);

	document.body.dataset.bodyNamespace =
		data.next.container.dataset.barbaNamespace;

	eventBus.emit('barba:enter', data);
});

barba.hooks.after(data => {
	if (ExperienceController) ExperienceController.init({ node: document.body });

	loadModule({
		module: ScrollText,
		id: 'scroll-text',
		node: document.body,
	});

	loadModule({
		module: ScrollColor,
		id: 'scroll-color',
		node: document.body,
	});

	loadModule({
		module: CardHover,
		id: 'card-hover',
		node: document.body,
	});

	loadModule({
		module: Footer,
		id: 'footer',
		node: document.getElementById('footer'),
	});

	ScrollTrigger.refresh();
});

htmx.on('htmx:afterSwap', ({ target, detail: { path } }) => {
	localLinks(target);
});

htmx.on('htmx:afterSettle', ({ target }) => {
	unloadModule('scroll-color');
	unloadModule('scroll-text');

	app.hydrate(target);

	loadModule({
		module: ScrollColor,
		id: 'scroll-color',
		node: document.body,
	});

	loadModule({
		module: ScrollText,
		id: 'scroll-text',
		node: document.body,
	});

	if (document.activeElement && document.activeElement === target) {
		document.activeElement.blur();
	}
	[...target.querySelectorAll('[data-scene]')].forEach(el => {
		if (ExperienceController) ExperienceController.observe(el);
	});
});

// Store the reference for removal
function throttle(fn, time) {
	let timeout = null;
	return function () {
		if (timeout) return;
		const context = this;
		const args = arguments;
		const later = () => {
			fn.call(context, ...args);
			timeout = null;
		};
		timeout = setTimeout(later, time);
	};
}

const breakpoint = 767;
let isMobileLayout = innerWidth <= breakpoint;
const resizeListener = throttle(() => {
	if (
		(isMobileLayout && innerWidth > breakpoint) ||
		(!isMobileLayout && innerWidth <= breakpoint)
	) {
		isMobileLayout = innerWidth <= breakpoint;
		barba.go(location.pathname);
	}
}, 300);
window.addEventListener('resize', resizeListener);

eventBus.on('App:paginate', ({ url, container }) => {
	unloadModule('load-more');

	app.hydrate(container);

	// loadModule({
	// 	module: LoadMore,
	// 	id: 'load-more',
	// 	node: container,
	// });

	[...container.querySelectorAll('[data-scene]')].forEach(el => {
		if (ExperienceController) ExperienceController.observe(el);
	});

	barba.history.add(url, 'barba', 'push');
});
